import i18n from "../language/i18n";

const colors = [
  "#FF5733", // Fiery Red
  "#3357FF", // Royal Blue
  "#FF33A1", // Fuchsia Pink
  "#33FFF0", // Aqua Blue
  "#FF9633", // Tangerine
  "#8D33FF", // Violet
  "#FFD733", // Sunny Yellow
  "#33FF6D", // Mint Green
  "#FFB833", // Amber
  "#33C7FF", // Sky Blue
  "#FF33E4", // Magenta
  "#5EFF33", // Neon Green
  "#FF3380", // Hot Pink
  "#33FFAF", // Seafoam Green
  "#3383FF", // Dodger Blue
  "#FF9533", // Pumpkin Orange
  "#BF33FF", // Electric Purple
  "#FFDA33", // Golden Yellow
  "#33FF89", // Turquoise
  "#FF3333", // Red
  "#33BFFF", // Light Blue
  "#FF33B3", // Pink
  "#5BFF33", // Light Green
  "#FF3385", // Rose
  "#33FFC9", // Aquamarine
  "#33A1FF", // Azure
  "#FF7A33", // Cantaloupe
  "#A533FF", // Lavender
  "#FFE033", // Mustard Yellow
  "#33FF7A", // Apple Green
  "#FF4D33", // Bright Red
  "#33FF95", // Light Turquoise
  "#FFC433", // Peach
  "#33FF7D", // Green Apple
  "#FF338A", // Raspberry Pink
  "#33FFE4", // Light Aqua
  "#336AFF", // Cobalt Blue
  "#FF3370", // Raspberry
  "#33FF95", // Light Green
  "#FFA633", // Orange
  "#33FF57", // Lime Green
];

export const shuffleArray = (array: any) => {
  const copyArray = JSON.parse(JSON.stringify(array));
  for (let i = copyArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copyArray[i], copyArray[j]] = [copyArray[j], copyArray[i]];
  }
  return copyArray;
};

// Deterministic string -> 32-bit seed (FNV-1a).
const hashStringToSeed = (value: string) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash = Math.imul(hash ^ value.charCodeAt(i), 16777619);
  }
  return hash >>> 0;
};

// Seeded PRNG (mulberry32) — same seed always yields the same sequence.
const mulberry32 = (seed: number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Deterministic Fisher-Yates keyed on a string seed. Unlike shuffleArray, the
// order is a pure function of (array, seedString) — so the SAME content always
// produces the SAME order across separate mounts / re-renders. Use when the
// shuffled order must survive an unmount/remount (e.g. an activity going from
// solve -> graded result), where Math.random() would re-seed to a new order.
export const seededShuffleArray = (array: any, seedString: string) => {
  const copyArray = JSON.parse(JSON.stringify(array));
  const random = mulberry32(hashStringToSeed(seedString));
  for (let i = copyArray.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copyArray[i], copyArray[j]] = [copyArray[j], copyArray[i]];
  }
  return copyArray;
};

export const getColorByIndex = (index: number) => {
  return colors[index];
};

export const retrieveLanguageOptionList = () => {
  return [
    { value: "TR", text: i18n.t("turkish") },
    { value: "EN", text: i18n.t("english") },
  ];
};

export const getLanguageFromCode = (code: string) => {
  if (code === "TR") {
    return "TURKISH";
  }
  return "ENGLISH";
};

// Foreign-language lessons should be explained in the language of the lesson,
// not the learner's UI language. The lesson's language is carried by its coterie
// type (derived from the category versionCode — e.g. CEFR-DE → GERMAN, CEFR/ENG
// → ENGLISH). Returns null for non-language subjects (MATHEMATICS, SCIENCE, …)
// and the ambiguous IB FOREIGN_LANGUAGE so the caller falls back to the system
// language. The backend prompt builder already supports GERMAN/ENGLISH/TURKISH.
export const getLanguageFromCoterieType = (
  coterieType: string,
): string | null => {
  if (coterieType === "GERMAN") {
    return "GERMAN";
  }
  if (coterieType === "ENGLISH") {
    return "ENGLISH";
  }
  return null;
};

export const getLanguageFromCurriculumType = (curriculumType: string) => {
  if (curriculumType === "MEB") {
    return "TURKISH";
  }
  return "ENGLISH";
};

export const getSystemLanguageFromCurriculumType = (curriculumType: string) => {
  if (curriculumType === "MEB") {
    return "TR";
  }
  return "EN";
};
